import React from "react";
import { Box, Typography, Tooltip } from "@mui/material";
import { useChartTokens } from "../../theme/chartTokens";

/**
 * Part-to-whole across a handful of states, as a single horizontal stacked bar.
 *
 * Built from layout boxes rather than a chart library so the 2px surface gaps
 * between segments are exact. Every segment is also spelled out in the list
 * below with its label, hours and share — the reserved amber "pending" colour
 * sits below 3:1 on a white surface, so status must never be carried by colour
 * alone.
 *
 * `data`: [{ status: string, hours: number }]
 */
export default function StatusBreakdown({ data, total }) {
  const t = useChartTokens();
  const sum = total ?? data.reduce((acc, d) => acc + d.hours, 0);

  if (!sum) return null;

  const pct = (hours) => (hours / sum) * 100;
  const labelFor = (status) =>
    String(status).charAt(0).toUpperCase() + String(status).slice(1);

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          gap: "2px",
          height: 12,
          borderRadius: 999,
          overflow: "hidden",
          mb: 2.5,
        }}
        role="img"
        aria-label={data
          .map((d) => `${labelFor(d.status)}: ${d.hours.toFixed(1)} hours`)
          .join(", ")}
      >
        {data.map((d) => (
          <Tooltip
            key={d.status}
            title={`${labelFor(d.status)} — ${d.hours.toFixed(2)} h (${pct(d.hours).toFixed(0)}%)`}
          >
            <Box
              sx={{
                width: `${pct(d.hours)}%`,
                bgcolor: t.statusFor(d.status),
                transition: "filter .15s ease",
                "&:hover": { filter: "brightness(1.1)" },
              }}
            />
          </Tooltip>
        ))}
      </Box>

      <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>
        {data.map((d) => (
          <Box key={d.status} sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
            <Box
              sx={{
                width: 10,
                height: 10,
                borderRadius: "50%",
                flexShrink: 0,
                bgcolor: t.statusFor(d.status),
              }}
            />
            <Typography variant="body2" sx={{ flexGrow: 1, minWidth: 0 }} noWrap>
              {labelFor(d.status)}
            </Typography>
            <Typography
              variant="body2"
              sx={{ fontWeight: 600, fontVariantNumeric: "tabular-nums" }}
            >
              {d.hours.toFixed(1)} h
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ width: 38, textAlign: "right", fontVariantNumeric: "tabular-nums" }}
            >
              {pct(d.hours).toFixed(0)}%
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
}
